import React, { useState, useMemo, useEffect } from 'react';
import stilePulsanti from '../../interfaccia/stilePulsanti';

const findLabelByValue = (nodes, value) => {
    for (const node of nodes) {
      if (node.value === value) {
        return node.label;
      }
      if (node.children) {
        const label = findLabelByValue(node.children, value);
        if (label) {
          return label;
        }
      }
    }
    return null;
  };

  const TreeNode = ({ node, onNodeToggle, onNodeSelect, selectedNodes, expandedNodes, depth = 0 }) => {
    const isExpanded = expandedNodes.includes(node.value);
    const hasChildren = Array.isArray(node.children) && node.children.length > 0;
  
    const handleToggle = () => {
      if (onNodeToggle) {
        onNodeToggle(node);
      }
    };
  
    const handleSelect = () => {
      if (onNodeSelect) {
        onNodeSelect(node);
      }
    };
  
    return (
      <li>
        <div style={{ '--depth': depth }}>
          {hasChildren ? (
            <button
              type="button"
              onClick={handleToggle}
              className={isExpanded ? 'expanded' : ''}
            />
          ) : (
            <span className="alberoPlaceholder" /> /* Freccia invisibile per i nodi senza figli */
          )}
          <input
            type="checkbox"
            checked={selectedNodes.includes(node.value)}
            onChange={handleSelect}
          />
          <span className="alberoLabel pointer" onClick={handleSelect}>
            {node.label}
          </span>
        </div>
        {isExpanded && hasChildren && (
          <ul>
            {node.children.map((child) => (
              <TreeNode
                key={child.value}
                node={child}
                onNodeToggle={onNodeToggle}
                onNodeSelect={onNodeSelect}
                selectedNodes={selectedNodes}
                expandedNodes={expandedNodes}
                depth={depth + 1} // Incrementa la profondità per i figli
              />
            ))}
          </ul>
        )}
      </li>
    );
  };

const isHomeNode = (node) => {
    const label = String(node?.label || "").trim();
    return /^(?:\d+\s*-\s*)?home$/i.test(label);
  };

  const getDefaultExpandedNodes = (nodes) =>
    (Array.isArray(nodes) ? nodes : [])
      .filter(isHomeNode)
      .map((node) => node.value);

const TreeView = ({ data, onChange, categorie }) => {
    const [selectedNodes, setSelectedNodes] = useState(categorie || []);
    const [searchTerm, setSearchTerm] = useState('');
    const [expandedNodes, setExpandedNodes] = useState(() => getDefaultExpandedNodes(data));
  
    useEffect(() => {
      // Inizializza i nodi selezionati con le categorie passate tramite props
      if (categorie) {
        setSelectedNodes(categorie);
      }
    }, [categorie]);
  
    const handleNodeSelect = (node) => {
      const isSelected = selectedNodes.includes(node.value);
      const newSelectedNodes = isSelected
        ? selectedNodes.filter((value) => value !== node.value)
        : [...selectedNodes, node.value];
  
      setSelectedNodes(newSelectedNodes);
      if (onChange) {
        onChange(node, newSelectedNodes);
      }
    };
  
    const handleDeselectNode = (value) => {
      const newSelectedNodes = selectedNodes.filter((nodeValue) => nodeValue !== value);
      setSelectedNodes(newSelectedNodes);
      if (onChange) {
        onChange(null, newSelectedNodes);
      }
    };
  
    const handleNodeToggle = (node) => {
      const isExpanded = expandedNodes.includes(node.value);
      const newExpandedNodes = isExpanded
        ? expandedNodes.filter((value) => value !== node.value)
        : [...expandedNodes, node.value];
  
      setExpandedNodes(newExpandedNodes);
    };
  
    const filterNodes = (nodes, term) => {
      return nodes
        .filter((node) => node.label.toLowerCase().includes(term.toLowerCase()) || (node.children && filterNodes(node.children, term).length > 0))
        .map((node) => ({
          ...node,
          children: node.children ? filterNodes(node.children, term) : [],
        }));
    };
  
    const expandAllFilteredNodes = (nodes) => {
      const expanded = [];
      const traverse = (nodeList) => {
        nodeList.forEach((node) => {
          const hasChildren = Array.isArray(node.children) && node.children.length > 0;
          if (hasChildren) {
            expanded.push(node.value);
            traverse(node.children);
          }
        });
      };
      traverse(nodes);
      return expanded;
    };
  
    const filteredData = useMemo(() => {
      return searchTerm ? filterNodes(data, searchTerm) : data;
    }, [data, searchTerm]);
  
  const areArraysEqual = (first, second) => {
    if (first === second) {
      return true;
    }
    if (!Array.isArray(first) || !Array.isArray(second)) {
      return false;
    }
    if (first.length !== second.length) {
      return false;
    }
    return first.every((value, index) => value === second[index]);
  };

  useEffect(() => {
    if (searchTerm) {
      const expanded = expandAllFilteredNodes(filteredData);
      setExpandedNodes((prev) =>
        areArraysEqual(prev, expanded) ? prev : expanded
      );
    }
    // non comprimere l'albero quando cambia data senza ricerca: evita refresh involontari dopo selezione nodi
  }, [searchTerm, filteredData]);
  
    return (
      <div className='alberoCategorie '>
        
  
        {/* Elenco dei nodi selezionati */}
        <div style={{ marginBottom: '10px' }}>
        {selectedNodes.map((value) => (
            <span key={value} className="selected-box">
            {findLabelByValue(data, value) || value}
            <button type="button" onClick={() => handleDeselectNode(value)}>×</button>
            </span>
        ))}
        </div>

        {/* Barra di ricerca */}
        <input
          type="text"
          placeholder="Cerca..."
          className={stilePulsanti.inputClassesNoMeasure}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{ marginBottom: '10px', padding: '5px', width: '100%' }}
        />
  
        {/* Albero */}
        <ul>
          {filteredData.map((node) => (
            <TreeNode
              key={node.value}
              node={node}
              onNodeToggle={handleNodeToggle}
              onNodeSelect={handleNodeSelect}
              selectedNodes={selectedNodes}
              expandedNodes={expandedNodes}
            />
          ))}
        </ul>
      </div>
    );
  };

export default TreeView;
